import React, { Component } from "react"
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap"
import OrderResultRecord from "../../api/OrderResults/OrderResultModel/OrderResultRecord"
import { toast } from "react-toastify"
import AuthFormProcessObservable from "../../api/OrderResults/AuthForm"
import printPdf from "../../utils/printPdf"
import FormatError from "../../formatters/FormatError"
import { EmailRegexp, PhoneRegexp } from "../../config/Constants"
import PhoneNumberMaskedInput from "../FormattedInputs/PhoneNumberMaskedInput"
import ActionButton from "../ActionButton"
import { FaMinus, FaPlus } from "react-icons/fa"
import { Subscription } from "rxjs"
import { AppState } from "../../AppState"
import { connect } from "react-redux"
import { BuildUserAccess } from "../../authentication/BuildUserAccess"

interface AuthFormModalProps {
  details?: OrderResultRecord
  onHide: () => void
  onEdit?: (data: OrderResultRecord) => void
  isAfterUpdate?: boolean
}

interface AuthFormModalAuthProps {
  canEditPendingOrders?: boolean
}

interface AuthFormModalState {
  emailItems: string[]
  mobileItems: string[]
  sendingEmail: boolean
  sendingText: boolean
}

class AuthFormModal extends Component<AuthFormModalProps & AuthFormModalAuthProps, AuthFormModalState> {
  state: AuthFormModalState = {
    emailItems: [""],
    mobileItems: [""],
    sendingEmail: false,
    sendingText: false
  }

  loadSubscription?: Subscription

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
  }

  handlePrintClick = (): void => {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    const params = {
      FormFoxTestAN: Number(this.props.details?.FormFoxTestAN || 0),
      PersonID: this.props.details?.PersonID || 0,
      VendorID: this.props.details?.CollectionVendorID || 0,
      SendToEmail: false,
      SendText: false,
      EmailAddress: []
    }
    this.loadSubscription = AuthFormProcessObservable(params).subscribe(
      data => printPdf(data),
      error => toast.error(FormatError(error))
    )
  }

  addEmail = (index: number, email: string = "") => {
    if (email !== this.state.emailItems[index]) {
      this.setState({ emailItems: [...this.state.emailItems, email] })
    }
  }

  removeEmail = (email: string, index: number) => {
    var array = [...this.state.emailItems]
    if (index > -1) {
      array.splice(index, 1)
      this.setState({ emailItems: array })
    }
  }

  changeEmail = (email: string, index: number) => {
    var array = [...this.state.emailItems]
    if (index > -1) {
      array[index] = email
      this.setState({ emailItems: array })
    }
  }

  sendEmail = () => {
    if (this.state.emailItems.filter(Boolean).length > 0) {
      if (Array.from(new Set(this.state.emailItems.map(value => value.toLowerCase()))).length !== this.state.emailItems.length) {
        toast.error("Duplicate email address is present in the list.")
        return
      }
      this.loadSubscription && this.loadSubscription.unsubscribe()
      const params = {
        EmailAddress: this.state.emailItems,
        FormFoxTestAN: Number(this.props.details?.FormFoxTestAN || 0),
        PersonID: this.props.details?.PersonID || 0,
        VendorID: this.props.details?.CollectionVendorID || 0,
        SendToEmail: true,
        SendText: false
      }
      this.setState({ sendingEmail: true })
      this.loadSubscription = AuthFormProcessObservable(params).subscribe(
        data => toast.success(data),
        error => toast.error(FormatError(error))
      )
      this.setState({ sendingEmail: false })
    }
    else {
      toast.error("Please enter email address.")
    }
  }

  isEmailValid = (value: string): boolean => {
    if (value.length === 0) {
      return true
    }
    return EmailRegexp.test(value)
  }

  canAddEmail = (email: string): boolean => email.length > 0 && this.isEmailValid(email)

  addMobile = (index: number, phone: string = "") => {
    if (phone !== this.state.mobileItems[index]) {
      this.setState({ mobileItems: [...this.state.mobileItems, phone] })
    }
  }

  removeMobile = (phone: string, index: number) => {
    var array = [...this.state.mobileItems]
    if (index > -1) {
      array.splice(index, 1)
      this.setState({ mobileItems: array })
    }
  }

  changeMobile = (phone: string, index: number) => {
    var array = [...this.state.mobileItems]
    if (index > -1) {
      array[index] = phone
      this.setState({ mobileItems: array })
    }
  }

  sendText = () => {
    if (this.state.mobileItems.filter(Boolean).length > 0) {
      if (Array.from(new Set(this.state.mobileItems.map(value => value.toLowerCase()))).length !== this.state.mobileItems.length) {
        toast.error("Duplicate mobile number is present in the list.")
        return
      }
      this.loadSubscription && this.loadSubscription.unsubscribe()
      const params = {
        EmailAddress: this.state.mobileItems,
        FormFoxTestAN: Number(this.props.details?.FormFoxTestAN || 0),
        PersonID: this.props.details?.PersonID || 0,
        VendorID: this.props.details?.CollectionVendorID || 0,
        SendToEmail: false,
        SendText: true
      }
      this.setState({ sendingText: true })
      this.loadSubscription = AuthFormProcessObservable(params).subscribe(
        data => toast.success("Text sent successfully"),
        error => toast.error(FormatError(error))
      )
      this.setState({ sendingText: false })
    }
    else {
      toast.error("Please enter mobile number.")
    }
  }

  isPhoneValid = (value: string): boolean => value.length === 0 || PhoneRegexp.test(value)
  canAddMobile = (mobile: string): boolean => mobile.length > 0 && this.isPhoneValid(mobile)

  render() {
    const { sendingEmail, sendingText, mobileItems, emailItems } = this.state
    const { details, onHide, canEditPendingOrders } = this.props

    return (
      <Modal show onHide={onHide} size="lg" dialogClassName="dark">
        <Modal.Header closeButton>
          <Modal.Title>
            {!this.props.isAfterUpdate && <h4 className="text-uppercase"><span>AUTHORIZATION #{ details?.FormFoxTestAN }</span></h4>}
            {!!this.props.isAfterUpdate && <h4 className="text-uppercase"><span>Resend Authorization #{ details?.FormFoxTestAN }</span></h4>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <Container fluid>
            <Row className="pb-2">
              <Col xs={3} lg={2}>Print form:</Col>
              <Col xs={9} lg={10} className="d-flex"><Button size="sm" className="fixed-w10" onClick={this.handlePrintClick}>Print PDF</Button></Col>
            </Row>
            {emailItems.map((email, index) => <Row className="pt-2" key={ `email-${index}` }>
                <Col xs={12} sm={3} md={3} lg={2}>
                  { index === 0 && "Email form:" }
                </Col>
                <Col xs={9} sm={5} md={4}>
                  <Form.Control size="sm" name="email_address" maxLength={ 40 } type="text" value={ email } onChange={ (e) => this.changeEmail(e.target.value, index) } readOnly={ emailItems.length !== index + 1 } isInvalid={ !this.isEmailValid(email) } />
                </Col>
                <Col xs={3} sm={2} md={2}>
                  { emailItems.length === index + 1 ? <Button size="sm" variant="primary" className="btn-sm-icon" onClick={ () => this.addEmail(index) } disabled={ !this.canAddEmail(email) }><FaPlus /></Button> : <Button size="sm" variant="primary" className="btn-sm-icon" onClick={ () => this.removeEmail(email, index) }><FaMinus /></Button> }
                </Col>
                {index === 0 && <Col xs={3} sm={2} md={2}><ActionButton size="sm" className="fixed-w10" onClick={ this.sendEmail } disabled={ emailItems.some(email => !this.isEmailValid(email)) || sendingEmail || sendingText} spin={sendingEmail}>Send Email</ActionButton></Col>}
              </Row>
            )}
            {mobileItems.map((mobile, index) => <Row className="pt-2" key={ `mobile-${index}` }>
                <Col xs={12} sm={3} md={3} lg={2}>
                  { index === 0 && "Text form:" }
                </Col>
                <Col xs={9} sm={5} md={4}>
                  <PhoneNumberMaskedInput size="sm" value={ mobile} onChange={(value: string) => this.changeMobile(value, index)} readOnly={ mobileItems.length !== index + 1 } isInvalid={ !this.isPhoneValid(mobile) } />
                </Col>
                <Col xs={3} sm={2} md={2}>
                  { mobileItems.length === index + 1 ? <Button size="sm" variant="primary" className="btn-sm-icon" onClick={ () => this.addMobile(index) } disabled={ !this.canAddMobile(mobile) }><FaPlus /></Button> : <Button size="sm" variant="primary" className="btn-sm-icon" onClick={ () => this.removeMobile(mobile, index) }><FaMinus /></Button> }
                </Col>
                {index === 0 && <Col xs={3} sm={2} md={2}><ActionButton size="sm" className="fixed-w10" onClick={ this.sendText } disabled={ mobileItems.some(mobile => !this.isPhoneValid(mobile)) || sendingEmail || sendingText } spin={sendingText}>Send Text</ActionButton></Col>}
              </Row>
            )}
          </Container>
        </Modal.Body>
        <Modal.Footer>
          {details && canEditPendingOrders && !this.props.isAfterUpdate && <Button size="sm" onClick={() => this.props.onEdit && this.props.onEdit(details)} className="mr-auto fixed-w5">Edit Order</Button>}
          <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

function mapStateToProps(state: AppState): AuthFormModalAuthProps {
  const userAccess = BuildUserAccess(state)
  return {
    canEditPendingOrders: userAccess.canEditPendingOrders
  }
}
const ConnectedAuthFormModal = connect(mapStateToProps, null)(AuthFormModal)
export default ConnectedAuthFormModal